/** 11Eleven brand mark — two signal-yellow slashes + Eleven lockup */
export function BrandMark({
  variant = 'dark',
  showLegal = true,
  product,
  size = 'md',
}: {
  variant?: 'dark' | 'light';
  showLegal?: boolean;
  product?: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const ink = variant === 'light' ? '#FFFFFF' : 'var(--color-ink)';
  const muted = variant === 'light' ? 'rgba(255,255,255,0.65)' : 'var(--color-steel)';
  const heights = { sm: 22, md: 32, lg: 48 };
  const h = heights[size];

  return (
    <div className={`brand-mark brand-${size}`} style={{ color: ink }}>
      <svg
        className="brand-slashes"
        width={Math.round(h * 0.72)}
        height={h}
        viewBox="0 0 28 40"
        aria-hidden
      >
        <rect x="2" y="2" width="7" height="36" rx="1.5" transform="skewX(-11)" fill="var(--color-signal)" />
        <rect x="14" y="2" width="7" height="36" rx="1.5" transform="skewX(-11)" fill="var(--color-signal)" />
      </svg>
      <div className="brand-text">
        <span className="brand-eleven">Eleven</span>
        {showLegal && <span className="brand-legal" style={{ color: muted }}>LOGISTICS INC.</span>}
      </div>
      {product && (
        <>
          <span className="brand-rule" aria-hidden />
          <span className="brand-product" style={{ color: ink }}>{product}</span>
        </>
      )}
      <style>{`
        .brand-mark {
          display: inline-flex;
          align-items: center;
          gap: 0.65rem;
          line-height: 1;
        }
        .brand-slashes { flex-shrink: 0; display: block; }
        .brand-text {
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
        }
        .brand-eleven {
          font-family: var(--font-display);
          font-weight: 700;
          letter-spacing: -0.02em;
        }
        .brand-sm .brand-eleven { font-size: 1rem; }
        .brand-md .brand-eleven { font-size: 1.35rem; }
        .brand-lg .brand-eleven { font-size: 2rem; }
        .brand-legal {
          font-family: var(--font-body);
          font-size: 0.55rem;
          font-weight: 500;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }
        .brand-sm .brand-legal { font-size: 0.45rem; }
        .brand-rule {
          width: 2px;
          align-self: stretch;
          min-height: 1.25em;
          background: var(--color-signal);
          flex-shrink: 0;
          margin: 0 0.15rem;
        }
        .brand-product {
          font-family: var(--font-display);
          font-weight: 500;
          letter-spacing: -0.01em;
        }
        .brand-sm .brand-product { font-size: 0.9375rem; }
        .brand-md .brand-product { font-size: 1.125rem; }
        .brand-lg .brand-product { font-size: 1.5rem; }
      `}</style>
    </div>
  );
}

/** Decorative slash field — use once on ink landing surfaces, at low emphasis */
export function SlashField({ className = '' }: { className?: string }) {
  return (
    <div className={`slash-field ${className}`} aria-hidden>
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className="slash-bar" />
      ))}
      <style>{`
        .slash-field {
          display: flex;
          gap: 0.45rem;
          transform: skewX(-11deg);
        }
        .slash-bar {
          display: block;
          width: 0.45rem;
          height: 2.75rem;
          background: var(--color-signal);
          border-radius: 1px;
        }
      `}</style>
    </div>
  );
}
